import React, { forwardRef, useMemo, useState } from 'react';

import { formatNumberFieldValue, isPresent } from '@aline/common';
import { useFragment } from '@apollo/client/react';
import { ButtonBase, Typography } from '@mui/material';
import type { GridCellParams, GridColDef } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { format, isValid, parseISO, startOfDay } from 'date-fns';
import { Lock } from 'lucide-react';
import { AlineTooltip, Spacer } from 'src/components/generic';
import type { PlayIconVariant } from 'src/components/icons/PlayIcon';
import { PlayIcon } from 'src/components/icons/PlayIcon';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import {
  getIsJobLocked,
  getIsReportRowQuestionJob,
  getLockedByTooltip,
  ReportCellPopover,
  useCellStyles,
  useReportCellPopoverOpen,
  useReportContext,
} from 'src/components/reports';
import {
  getIsExtractionJobFailed,
  getIsExtractionJobRunning,
  getIsExtractionJobStale,
  OptionItemChip,
} from 'src/editor';
import {
  getOCRStatusFromFileVersion,
  OCRStatus,
} from 'src/editor/toolbars/right-panel/ai/shared/OCRStatusOrChildren';
import { Apollo } from 'src/gql/generated';
import { documentTypename, doNothing } from 'src/helpers';
import { useRunExtractionJobsForDocuments } from 'src/hooks';
import type { DeepPartial } from 'ts-essentials';
import { makeStyles } from 'tss-react/mui';

import type { QuestionJobOption, ReportGridRow } from '../types';

export const useExtractionCellStyles = makeStyles()(theme => ({
  errorCell: {
    backgroundColor: `rgba(232, 20, 134, 0.1)`,
  },
  lockIcon: {
    color: theme.palette.grey[500],
    flexShrink: 0,
    height: 16,
    width: 16,
  },
  notYetRunCell: {
    backgroundColor: `rgba(232, 122, 20, 0.1)`,
  },
  staleCell: {
    backgroundColor: 'rgba(232, 211, 20, 0.1)',
  },
}));

function LockedCellIcon({ title }: { title: string }): JSX.Element {
  const { classes } = useExtractionCellStyles();

  return (
    <AlineTooltip arrow placement="top" title={title}>
      <Lock className={classes.lockIcon} />
    </AlineTooltip>
  );
}

interface ReportCellValueProps {
  colDef: Pick<GridColDef<ReportGridRow>, 'type'>;
  onClickCell?: (e: React.MouseEvent) => void;
  value?: string | number | boolean | Date | null;
}

export const ReportCellValue = forwardRef(function ReportCellValue(
  {
    colDef,
    isLocked = false,
    lockTooltip = 'This cell is locked',
    onClickCell = doNothing,
    value,
  }: ReportCellValueProps & { isLocked?: boolean; lockTooltip?: string },
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const classes = useCellStyles();
  const { cellValue } = useReportCellValue({ colDef, value });

  return (
    <div ref={ref} className={classes.cell} onClick={onClickCell}>
      <Typography noWrap>{cellValue.join('')}</Typography>
      {isLocked && (
        <>
          <Spacer />
          <LockedCellIcon title={lockTooltip} />
        </>
      )}
    </div>
  );
});

export function useReportCellValue({
  colDef,
  value,
}: Pick<ReportCellValueProps, 'colDef' | 'value'>): {
  cellValue: string[];
} {
  if (value == null || value === '') return { cellValue: ['-'] };
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return { cellValue: ['-'] };
    return { cellValue: [value.toLocaleString()] };
  }
  if (value instanceof Date) return { cellValue: [format(value, 'MMM d, yyyy')] };
  if (typeof value === 'boolean') return { cellValue: [value ? 'Yes' : 'No'] };
  if (typeof value !== 'string') return { cellValue: ['-'] };

  switch (colDef.type) {
    case 'date':
    case 'dateTime': {
      const date = parseISO(value);
      return {
        cellValue: [isValid(date) ? format(date, 'MMM d, yyyy') : '-'],
      };
    }
    case 'number':
      return { cellValue: [formatNumberFieldValue(value)] };
    case 'boolean': {
      return { cellValue: [value === 'true' ? 'Yes' : 'No'] };
    }
    default:
      return { cellValue: value.split('\n') };
  }
}

export function getExtractionCellValue(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  val: any,
  type: Apollo.ReportColumnDataType
): Date | string | number | boolean | null {
  if (!getIsReportRowQuestionJob(val)) return null;

  switch (type) {
    case Apollo.ReportColumnDataType.Date:
    case Apollo.ReportColumnDataType.DateTime: {
      return val.answer ? startOfDay(parseISO(val.answer)) : null;
    }
    case Apollo.ReportColumnDataType.Number: {
      return val.answer || null;
    }
    case Apollo.ReportColumnDataType.Boolean:
      return val.answer === 'true' ? true : false;
    default:
      return val.answer ?? null;
  }
}

export function ExtractionCell({ colDef, field: questionId, row }: GridCellParams<ReportGridRow>) {
  const { questions } = useReportContext();
  const popoverId = useMemo(() => `${row.id}:${questionId}`, [questionId, row.id]);
  const { open, setOpen } = useReportCellPopoverOpen(popoverId);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  // Subscribe to the live Document cache so the cell re-renders as extraction
  // jobs finish. Spread cached over row so fresh cell-state fields win while
  // popover-only fields still come from the row.
  const { complete: cacheHit, data: cached } = useFragment<Apollo.DocumentExtractionStatusFragment>(
    {
      fragment: Apollo.DocumentExtractionStatusFragmentDoc,
      fragmentName: Apollo.namedOperations.Fragment.DocumentExtractionStatus,
      from: { __typename: documentTypename, id: row.documentId },
    }
  );
  const aiQuestionIdsQueueing = cacheHit ? cached.aiQuestionIdsQueueing : row.aiQuestionIdsQueueing;
  const rowExtractionJob =
    questionId in row.questionJobsByQuestionId ? row.questionJobsByQuestionId[questionId] : null;
  const cachedExtractionJob = cacheHit
    ? (cached.questionJobs.find(j => j.question?.id === questionId) ?? null)
    : null;
  /**
   * Memoized so the (memoized) popover skips re-renders when a sibling cell's
   * job updates. The shared Document useFragment re-renders every cell of a
   * document whenever any one of its jobs changes during an AI run; a fresh
   * spread object here would defeat the popover's React.memo.
   */
  const extractionJob = useMemo(
    () =>
      cachedExtractionJob ? { ...rowExtractionJob, ...cachedExtractionJob } : rowExtractionJob,
    [cachedExtractionJob, rowExtractionJob]
  );
  const question = questions.find((q: { id: string }) => q.id === questionId);
  const reportRowQuestion = useMemo(() => ({ question }), [question]);
  const ocrStatus = getOCRStatusFromFileVersion(row.latestVersion);
  /**
   * A column with inputs answers from them, never from the document, so a failed OCR
   * can't block it. OCR still running does: the server holds every question until it ends.
   */
  const readsDocument = !question?.inputs?.length && !question?.inputFileFields?.length;
  const isAiQuestionQueueing = aiQuestionIdsQueueing.includes(questionId);
  const isLocked = getIsJobLocked(extractionJob);

  /**
   * Manual-entry columns don't use OCR or AI extraction jobs, so they ignore the
   * queueing / OCR / running / error states below — just render the (editable)
   * answer, empty when nothing has been entered yet.
   */
  if (question?.mode === Apollo.ExtractionQuestionMode.Manual) {
    const manualJob = getIsReportRowQuestionJob(extractionJob) ? extractionJob : null;
    return (
      <>
        <ExtractionCellAnswer
          answerInfo={{
            answer: manualJob?.answer ?? '',
            selectedOptions: manualJob?.selectedOptions?.filter(isPresent) ?? [],
            type: question.type,
          }}
          colDef={colDef}
          isLocked={isLocked}
          lockTooltip={getLockedByTooltip(extractionJob, 'This cell is locked')}
          setAnchorEl={setAnchorEl}
          setOpen={setOpen}
        />
        <ReportCellPopover
          anchorEl={anchorEl}
          documentId={row.id}
          extractionJob={manualJob ?? reportRowQuestion}
          open={open}
          setOpen={setOpen}
        />
      </>
    );
  }

  // Note order MATTERS here so be careful modifying order.
  if (isAiQuestionQueueing) return <ExtractionRunningCell />;
  if (readsDocument && ocrStatus === OCRStatus.Failed) return <OCRErrorCell documentId={row.id} />;
  if (ocrStatus === OCRStatus.Running) return <OCRRunningCell />;
  if (!getIsReportRowQuestionJob(extractionJob)) {
    return <ExtractionNotRunCell documentId={row.id} questionId={questionId} />;
  }
  if (getIsExtractionJobRunning(extractionJob)) return <ExtractionRunningCell />;
  // Locked jobs can't be re-run, so failed/stale UI would invite a click that does nothing.
  // Render the persisted answer (possibly empty) instead — the lock icon still communicates state.
  if (!isLocked && getIsExtractionJobFailed(extractionJob))
    return <ExtractionErrorCell documentId={row.id} questionId={questionId} />;
  if (!isLocked && getIsExtractionJobStale(row, extractionJob))
    return <ExtractionStaleCell documentId={row.id} questionId={questionId} />;

  return (
    <>
      <ExtractionCellAnswer
        answerInfo={{
          answer: extractionJob.answer ?? '',
          selectedOptions: extractionJob.selectedOptions?.filter(isPresent) ?? [],
          type: extractionJob.question?.type ?? Apollo.ExtractionQuestionType.Text,
        }}
        colDef={colDef}
        isLocked={isLocked}
        lockTooltip={getLockedByTooltip(extractionJob, 'This cell is locked')}
        setAnchorEl={setAnchorEl}
        setOpen={setOpen}
      />
      <ReportCellPopover
        anchorEl={anchorEl}
        documentId={row.id}
        extractionJob={extractionJob}
        open={open}
        setOpen={setOpen}
      />
    </>
  );
}

interface ExtractionIconProps {
  mouseOver: boolean;
  onClick: (e: React.MouseEvent) => void;
  variant: PlayIconVariant;
}

function ExtractionIcon({ mouseOver, onClick, variant }: ExtractionIconProps): JSX.Element {
  if (!mouseOver) return <PulseIcon variant={variant} />;

  // Stop propagation so the enclosing DataGrid row's onRowClick doesn't fire
  // and open the document in a new tab while the user is starting extraction.
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(e);
  };

  return (
    <ButtonBase onClick={handleClick}>
      <PlayIcon variant={variant} />
    </ButtonBase>
  );
}

interface ExtractionNotRunCellProps {
  documentId: UUID;
  questionId: UUID;
}

export function ExtractionNotRunCell({
  documentId,
  questionId,
}: ExtractionNotRunCellProps): JSX.Element {
  const classes = useCellStyles();
  const { classes: extractionCellClasses } = useExtractionCellStyles();
  const [mouseOver, setMouseOver] = useState<boolean>(false);
  const { startExtractionJobsForDocuments } = useRunExtractionJobsForDocuments();

  return (
    <div
      className={clsx(classes.cell, extractionCellClasses.notYetRunCell)}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <Typography>AI not run.</Typography>
      <Spacer />
      <ExtractionIcon
        mouseOver={mouseOver}
        variant={PulseIconVariant.WarnAmber}
        onClick={async () => {
          await startExtractionJobsForDocuments({
            variables: {
              input: { documentIds: [documentId], questionIds: [questionId] },
            },
          });
        }}
      />
    </div>
  );
}

export function OCRRunningCell({
  pulsePosition = 'right',
}: {
  pulsePosition?: 'left' | 'right';
}): JSX.Element {
  const classes = useCellStyles();

  if (pulsePosition === 'left') {
    return (
      <div className={classes.cell}>
        <PulseIcon variant={PulseIconVariant.LoadingSecondary} />
        <Typography>OCR is running...</Typography>
        <Spacer />
      </div>
    );
  }

  return (
    <div className={classes.cell}>
      <Typography>OCR is running...</Typography>
      <Spacer />
      <PulseIcon variant={PulseIconVariant.LoadingSecondary} />
    </div>
  );
}

interface OCRErrorCellProps {
  documentId: UUID;
}

export function OCRErrorCell({ documentId }: OCRErrorCellProps): JSX.Element {
  const classes = useCellStyles();
  const { classes: extractionCellClasses } = useExtractionCellStyles();
  const [mouseOver, setMouseOver] = useState<boolean>(false);
  const [startOCR] = Apollo.useStartOcrForDocumentsMutation();

  return (
    <div
      className={clsx(classes.cell, extractionCellClasses.errorCell)}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <Typography>OCR error. Rerun to fix.</Typography>
      <Spacer />
      <ExtractionIcon
        mouseOver={mouseOver}
        variant={PulseIconVariant.Error}
        onClick={async () => {
          await startOCR({ variables: { documentIds: [documentId] } });
        }}
      />
    </div>
  );
}

export function ExtractionRunningCell(): JSX.Element {
  const classes = useCellStyles();

  return (
    <div className={classes.cell}>
      <Typography>Aline AI thinking...</Typography>
      <Spacer />
      <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
    </div>
  );
}

interface ExtractionErrorCellProps {
  documentId: UUID;
  questionId: UUID;
}

/**
 * Only rendered when the job is NOT locked — the caller (`ExtractionCell`)
 * guards on `!isLocked` before returning this component, so the rerun play
 * icon is always safe to render here.
 */
export function ExtractionErrorCell({
  documentId,
  questionId,
}: ExtractionErrorCellProps): JSX.Element {
  const classes = useCellStyles();
  const { classes: extractionCellClasses } = useExtractionCellStyles();
  const [mouseOver, setMouseOver] = useState<boolean>(false);
  const { startExtractionJobsForDocuments } = useRunExtractionJobsForDocuments();

  return (
    <div
      className={clsx(classes.cell, extractionCellClasses.errorCell)}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <Typography>Error. Rerun to fix.</Typography>
      <Spacer />
      <ExtractionIcon
        mouseOver={mouseOver}
        variant={PulseIconVariant.Error}
        onClick={async () => {
          await startExtractionJobsForDocuments({
            variables: {
              input: { documentIds: [documentId], questionIds: [questionId] },
            },
          });
        }}
      />
    </div>
  );
}

interface ExtractionStaleCellProps {
  documentId: UUID;
  questionId: UUID;
}

/**
 * Only rendered when the job is NOT locked — see ExtractionErrorCell for the
 * same contract with the caller.
 */
export function ExtractionStaleCell({
  documentId,
  questionId,
}: ExtractionStaleCellProps): JSX.Element {
  const classes = useCellStyles();
  const { classes: extractionCellClasses } = useExtractionCellStyles();
  const [mouseOver, setMouseOver] = useState<boolean>(false);
  const { startExtractionJobsForDocuments } = useRunExtractionJobsForDocuments();

  return (
    <div
      className={clsx(classes.cell, extractionCellClasses.staleCell)}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      <Typography>AI answer stale. Rerun.</Typography>
      <Spacer />
      <ExtractionIcon
        mouseOver={mouseOver}
        variant={PulseIconVariant.WarnYellow}
        onClick={async () => {
          await startExtractionJobsForDocuments({
            variables: {
              input: { documentIds: [documentId], questionIds: [questionId] },
            },
          });
        }}
      />
    </div>
  );
}

function ExtractionCellAnswer({
  answerInfo,
  colDef,
  isLocked = false,
  lockTooltip = 'This cell is locked',
  setAnchorEl,
  setOpen,
}: {
  answerInfo: {
    answer: string;
    selectedOptions: DeepPartial<QuestionJobOption>[];
    type: Apollo.ExtractionQuestionType;
  };
  colDef: GridColDef<ReportGridRow>;
  setAnchorEl: (anchorEl: HTMLDivElement | null) => void;
  setOpen: (open: boolean) => void;
  isLocked?: boolean;
  lockTooltip?: string;
}): JSX.Element {
  const classes = useCellStyles();
  const handleClickCell = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
  };
  const lockSuffix = isLocked ? (
    <>
      <Spacer />
      <LockedCellIcon title={lockTooltip} />
    </>
  ) : null;

  switch (answerInfo.type) {
    case Apollo.ExtractionQuestionType.SingleSelect:
    case Apollo.ExtractionQuestionType.MultiSelect: {
      if (!answerInfo.selectedOptions.length)
        return (
          <div ref={setAnchorEl} className={classes.cell} onClick={handleClickCell}>
            <Typography noWrap>-</Typography>
            {lockSuffix}
          </div>
        );

      return (
        <div ref={setAnchorEl} className={classes.cell} onClick={handleClickCell}>
          {answerInfo.selectedOptions.map((option, index) => (
            <OptionItemChip key={option?.id ?? index} option={option ?? {}} />
          ))}
          {lockSuffix}
        </div>
      );
    }
    case Apollo.ExtractionQuestionType.Boolean: {
      const displayValue =
        answerInfo.answer === 'true' ? 'Yes' : answerInfo.answer === 'false' ? 'No' : '-';

      return (
        <div ref={setAnchorEl} className={classes.cell} onClick={handleClickCell}>
          <Typography noWrap>{displayValue}</Typography>
          {lockSuffix}
        </div>
      );
    }
    default: {
      if (typeof answerInfo.answer !== 'string')
        return (
          <div ref={setAnchorEl} className={classes.cell} onClick={handleClickCell}>
            <Typography noWrap>-</Typography>
            {lockSuffix}
          </div>
        );

      return (
        <ReportCellValue
          ref={setAnchorEl}
          colDef={colDef}
          isLocked={isLocked}
          lockTooltip={lockTooltip}
          value={answerInfo.answer}
          onClickCell={handleClickCell}
        />
      );
    }
  }
}
